import * as Leaflet from 'leaflet';
import 'leaflet-draw';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CircleShapeOptions,
  Coordinates,
  GeofenceConnector,
  MapShape,
  PolygonShapeOptions,
} from '../interfaces';
import { MapShapeType } from '../constants/constants';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable()
export class ShapesService {
  private translate = inject(TranslateService);

  shapeLayers: (Leaflet.Circle<unknown> | Leaflet.Polygon<unknown>)[] = [];
  drawnShapes: L.FeatureGroup = Leaflet.featureGroup();

  /** dot + dashed line connecting a temporary geofence to the vehicle it belongs to,
   * keyed by that geofence's circle layer so visibility mirrors the circle's own. */
  private connectorLayers = new Map<
    Leaflet.Circle<unknown> | Leaflet.Polygon<unknown>,
    GeofenceConnector
  >();

  initShapes(
    map: Leaflet.Map,
    shapes: MapShape[],
    isDrawingEnabled?: boolean
  ): void {
    shapes.forEach(shape => {
      const isTemporary = shape.color === 'blue';
      switch (shape.type) {
        case MapShapeType.Polygon: {
          const polygon = Leaflet.polygon(shape.points!, {
            ...this.getShapeOptions(isTemporary),
            id: shape.id,
          } as PolygonShapeOptions);
          this.shapeLayers.push(polygon);
          map.addLayer(polygon);
          if (shapes.length === 1) {
            map.fitBounds(polygon.getBounds());
          }

          if (isDrawingEnabled) {
            this.drawnShapes.addLayer(polygon);
          }
          break;
        }
        case MapShapeType.Circle: {
          if (
            !shape.center ||
            !shape.center.lat ||
            !shape.center.lng ||
            !shape.radius
          )
            return;

          const circle = new Leaflet.Circle(shape.center!, shape.radius!, {
            ...this.getShapeOptions(isTemporary),
            radius: shape.radius,
            id: shape.id,
          } as CircleShapeOptions);

          if (shapes.length === 1 && !shape.skipFitBounds) {
            circle.on('add', () => {
              map.fitBounds(circle.getBounds());
            });
          }
          this.shapeLayers.push(circle);
          map.addLayer(circle);

          if (
            isTemporary &&
            shape.linkedVehicleId !== undefined &&
            shape.linkedPosition
          ) {
            const dot = Leaflet.circleMarker(shape.center!, {
              radius: 5,
              color: 'blue',
              fillColor: 'blue',
              fillOpacity: 1,
              weight: 1,
              interactive: false,
            });
            const line = Leaflet.polyline(
              [shape.center!, shape.linkedPosition],
              {
                color: 'blue',
                weight: 2,
                dashArray: '6, 6',
                interactive: false,
              }
            );
            map.addLayer(dot);
            map.addLayer(line);
            this.connectorLayers.set(circle, {
              dot,
              line,
              linkedVehicleId: shape.linkedVehicleId,
            });
          }

          if (isDrawingEnabled) {
            this.drawnShapes.addLayer(circle);
          }
          break;
        }
      }
    });
  }

  /** Hides shapes whose bounds fully cover the current viewport (e.g. a country-sized
   * geofence while zoomed into a single city) since they'd render as an opaque overlay
   * with no visible border and add no useful information. Shapes being drawn/edited via
   * the draw toolbar are left untouched. */
  updateShapeVisibilityForViewport(map: Leaflet.Map): void {
    const viewportBounds = map.getBounds();
    const drawnLayers = this.drawnShapes.getLayers();

    this.shapeLayers.forEach(layer => {
      if (drawnLayers.includes(layer)) return;

      const coversViewport =
        this.getShapeGeoBounds(layer).contains(viewportBounds);
      const connector = this.connectorLayers.get(layer);
      if (coversViewport && map.hasLayer(layer)) {
        map.removeLayer(layer);
        if (connector) {
          map.removeLayer(connector.dot);
          map.removeLayer(connector.line);
        }
      } else if (!coversViewport && !map.hasLayer(layer)) {
        map.addLayer(layer);
        if (connector) {
          map.addLayer(connector.dot);
          map.addLayer(connector.line);
        }
      }
    });
  }

  /** Keeps each temporary geofence's connector line pointed at its vehicle's current
   * position as marker data refreshes, without re-rendering the geofence shapes themselves. */
  updateConnectorPositions(
    positionsByVehicleId: Map<number, Coordinates>
  ): void {
    this.connectorLayers.forEach(({ line, linkedVehicleId }) => {
      const position = positionsByVehicleId.get(linkedVehicleId);
      if (!position) return;

      const [start] = line.getLatLngs() as Leaflet.LatLng[];
      line.setLatLngs([start, position]);
    });
  }

  drawShape(
    event: Leaflet.DrawEvents.Created,
    map: Leaflet.Map
  ): MapShape | undefined {
    let newShape: MapShape | undefined;
    const type = event.layerType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layer: any = event.layer;

    if (type === 'polygon') {
      const polygonCoordinates = layer._latlngs[0];
      newShape = {
        type: MapShapeType.Polygon,
        points: polygonCoordinates,
      };
    }

    if (type === 'circle') {
      newShape = {
        type: MapShapeType.Circle,
        radius: layer.options.radius,
        center: layer._latlng,
      };
    }
    this.deleteShapes(map);

    map.addLayer((event as Leaflet.DrawEvents.Created).layer);
    this.shapeLayers.push(
      event.layer as Leaflet.Circle<unknown> | Leaflet.Polygon<unknown>
    );
    this.drawnShapes.addLayer(event.layer);
    return newShape;
  }

  deleteShapes(map: Leaflet.Map): void {
    this.shapeLayers.forEach(shapeLayer => map.removeLayer(shapeLayer));
    this.shapeLayers = [];
    this.connectorLayers.forEach(({ dot, line }) => {
      map.removeLayer(dot);
      map.removeLayer(line);
    });
    this.connectorLayers.clear();
  }

  editShape(
    event: Leaflet.DrawEvents.Edited,
    shape?: MapShapeType
  ): MapShape | undefined {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editShapeEvent = event as any;
    let editedShape: MapShape | undefined;
    const shapeEditLayer =
      editShapeEvent.layers._layers[
        Object.keys(editShapeEvent.layers._layers)[0]
      ];
    switch (shape) {
      case MapShapeType.Circle:
        editedShape = {
          type: MapShapeType.Circle,
          radius: shapeEditLayer._mRadius,
          center: shapeEditLayer._latlng,
        };
        break;
      case MapShapeType.Polygon:
        editedShape = {
          type: MapShapeType.Polygon,
          points: shapeEditLayer._latlngs[0],
        };
        break;
    }
    return editedShape;
  }

  getShapeDrawingOptions(): Leaflet.Control.DrawConstructorOptions {
    return {
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: 'orange',
            showArea: true,
          },
        },
        circle: {
          shapeOptions: {
            color: 'orange',
            showArea: true,
          },
        },
      },
      edit: {
        poly: { allowIntersection: false },
        featureGroup: this.drawnShapes,
        remove: false,
      },
    } as Leaflet.Control.DrawConstructorOptions;
  }

  triggerDrawPolygon(): void {
    const drawPanelPolygonElement = document.getElementsByClassName(
      'leaflet-draw-draw-polygon'
    )[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (drawPanelPolygonElement as any)?.click();
  }

  triggerDrawCircle(): void {
    const drawPanelCircleElement = document.getElementsByClassName(
      'leaflet-draw-draw-circle'
    )[0];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (drawPanelCircleElement as any)?.click();
  }

  setShapeDrawingOptions(): Leaflet.Control.DrawConstructorOptions | undefined {
    Leaflet.drawLocal.draw.handlers.polygon = this.getPolygonDrawHandlers();
    Leaflet.drawLocal.draw.handlers.polyline = this.getPolyLineDrawHandlers();
    Leaflet.drawLocal.draw.handlers.circle = this.getCircleDrawHandlers();
    Leaflet.drawLocal.edit.handlers.edit = this.getEditHandlers();
    Leaflet.drawLocal.edit.toolbar.buttons = this.getEditToolbarButtonTexts();
    Leaflet.drawLocal.edit.toolbar.actions = this.getEditToolbarActionTexts();
    Leaflet.drawLocal.draw.toolbar = this.getDrawToolbarTexts();

    return this.getShapeDrawingOptions();
  }

  listenToShapeDrawEvents(
    enableShapeDrawing: boolean,
    map: Leaflet.Map,
    shapeDrawTrigger$: Subject<MapShape> | undefined,
    shapes$: BehaviorSubject<MapShape[] | undefined> | undefined
  ): void {
    if (!enableShapeDrawing || !shapeDrawTrigger$) return;

    const drawShapeControl = new Leaflet.Control.Draw(
      this.setShapeDrawingOptions()
    );
    map.addControl(drawShapeControl);

    map.on('draw:created', event => {
      const newShape = this.drawShape(event as Leaflet.DrawEvents.Created, map);
      if (newShape) {
        shapeDrawTrigger$.next(newShape);
      }
    });
    map.on('draw:edited', event => {
      const editableShape = shapes$?.getValue()?.[0];
      if (!editableShape) return;
      const editedShape = this.editShape(
        event as Leaflet.DrawEvents.Edited,
        // draw is supported for single shape only
        editableShape.type
      );

      if (editedShape) {
        shapeDrawTrigger$.next(editedShape);
      }
    });
  }

  /** https://github.com/Leaflet/Leaflet.draw/issues/945 */
  fixCircleEditRadiusIssue(): void {
    (window as any).radius = undefined;
  }

  /** Circle.getBounds() reads pixel coordinates off the map projection, so it throws once
   * the circle has been removed from the map (its internal `_map` reference is null). Compute
   * bounds from the circle's own lat/lng + radius instead, which works whether or not it's
   * currently attached. Polygon bounds are cached from the raw latlngs and stay map-independent. */
  getShapeGeoBounds(
    layer: Leaflet.Circle<unknown> | Leaflet.Polygon<unknown>
  ): Leaflet.LatLngBounds {
    return layer instanceof Leaflet.Circle
      ? layer.getLatLng().toBounds(layer.getRadius() * 2)
      : layer.getBounds();
  }

  private getPolyLineDrawHandlers(): Leaflet.Localization.DrawHandlers['polyline'] {
    return {
      error: this.translate.instant('Map.DrawPolyline.Error'),
    } as Leaflet.Localization.DrawHandlers['polyline'];
  }

  private getCircleDrawHandlers(): Leaflet.Localization.DrawHandlers['circle'] {
    return {
      tooltip: {
        start: this.translate.instant('Map.DrawCircle.Start'),
      },
      radius: this.translate.instant('Map.Radius'),
    };
  }

  private getPolygonDrawHandlers(): Leaflet.Localization.DrawHandlers['polygon'] {
    return {
      tooltip: {
        start: this.translate.instant('Map.DrawPolygon.Start'),
        cont: this.translate.instant('Map.DrawPolygon.Continue'),
        end: this.translate.instant('Map.DrawPolygon.End'),
      },
    };
  }

  private getEditHandlers(): Leaflet.Localization.EditHandlers['edit'] {
    return {
      tooltip: {
        text: this.translate.instant('Map.EditHandler.Text'),
        subtext: this.translate.instant('Map.EditHandler.Title'),
      },
    };
  }

  private getEditToolbarButtonTexts(): Leaflet.Localization.EditToolbar['buttons'] {
    return {
      edit: this.translate.instant('Map.Shape.Edit'),
      editDisabled: this.translate.instant('Map.Shape.EditDisabled'),
      remove: '',
      removeDisabled: '',
    };
  }

  private getEditToolbarActionTexts(): Leaflet.Localization.EditToolbar['actions'] {
    return {
      save: {
        title: this.translate.instant('Map.Toolbar.SaveChangesText'),
        text: this.translate.instant('General.Save'),
      },
      cancel: {
        title: this.translate.instant('Map.Toolbar.CancelChangesText'),
        text: this.translate.instant('General.Cancel'),
      },
      clearAll: { title: '', text: '' },
    };
  }

  private getDrawToolbarTexts(): Leaflet.Localization.DrawToolbar {
    return {
      actions: {
        text: this.translate.instant('General.Cancel'),
        title: '',
      },
      finish: {
        text: this.translate.instant('Map.Draw.FinishText'),
        title: '',
      },
      undo: {
        text: this.translate.instant('Map.Draw.UndoText'),
        title: '',
      },
      buttons: {
        polyline: '',
        polygon: '',
        rectangle: '',
        circle: '',
        marker: '',
        circlemarker: '',
      },
    };
  }

  private getShapeOptions(
    isTemporary = false
  ): Leaflet.PolylineOptions | Leaflet.CircleOptions {
    const color = isTemporary ? 'blue' : 'orange';
    return {
      color,
      fillColor: color,
      fillOpacity: 0.15,
      opacity: 0.9,
      weight: 2,
      dashArray: isTemporary ? '8, 6' : undefined,
    };
  }
}
